'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '../utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

/** Positions the content against an element that isn't the trigger (a search input). */
const PopoverAnchor = PopoverPrimitive.Anchor;

/** Read keyboard overlap (px) at the bottom of the layout viewport. Zero when no soft keyboard / no visualViewport API. */
function readKeyboardOverlapPx(): number {
  if (typeof window === 'undefined') return 0;
  const vv = window.visualViewport;
  if (!vv) return 0;
  return Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
}

type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> & {
  /**
   * When true (default) and the soft keyboard is open, extends Radix's `collisionPadding` so the
   * popover flips above the trigger if it would otherwise sit behind the keyboard, and clamps
   * `maxHeight` to the visual viewport so the content scrolls instead of being occluded.
   */
  keepFocusVisibleAboveKeyboard?: boolean;
};

const PopoverContent = ({
  className,
  align = 'center',
  sideOffset = 4,
  collisionPadding,
  keepFocusVisibleAboveKeyboard = true,
  style,
  ...props
}: PopoverContentProps) => {
  const [keyboardOverlapPx, setKeyboardOverlapPx] = React.useState(0);

  React.useEffect(() => {
    if (!keepFocusVisibleAboveKeyboard) {
      setKeyboardOverlapPx(0);
      return;
    }
    const update = () => setKeyboardOverlapPx(readKeyboardOverlapPx());
    update();
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [keepFocusVisibleAboveKeyboard]);

  const mergedCollisionPadding = React.useMemo(() => {
    if (!keepFocusVisibleAboveKeyboard || keyboardOverlapPx === 0) return collisionPadding;
    if (typeof collisionPadding === 'number') {
      return {
        top: collisionPadding,
        left: collisionPadding,
        right: collisionPadding,
        bottom: collisionPadding + keyboardOverlapPx,
      };
    }
    return {
      ...(collisionPadding ?? {}),
      bottom: (collisionPadding?.bottom ?? 0) + keyboardOverlapPx,
    };
  }, [collisionPadding, keepFocusVisibleAboveKeyboard, keyboardOverlapPx]);

  const mergedStyle: React.CSSProperties | undefined =
    keepFocusVisibleAboveKeyboard &&
    keyboardOverlapPx > 0 &&
    typeof window !== 'undefined' &&
    window.visualViewport
      ? {
          ...(style ?? {}),
          maxHeight: `${Math.max(120, window.visualViewport.height - 32)}px`,
          overflowY: 'auto',
        }
      : style;

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={mergedCollisionPadding}
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 rounded-md border bg-white p-4 shadow-md outline-none',
          className,
        )}
        style={mergedStyle}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
};
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
